import { Component, inject, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BackendService } from '@core/services';
import { BaseComponent, DialogRef } from '@shared/classes';
import { ButtonComponent } from '@shared/components/button/button.component';
import { CampaignPhase, OfferType } from '@shared/constants';
import {
  AnyChallenge,
  DIALOG_DATA,
  DialogCloseButton,
  DialogConfig,
  Dict,
  EmitterKeys,
  UnselectionDialog,
} from '@shared/models';
import { SafePipe, TransformUrlToBgPipe } from '@shared/pipes';

interface DialogData {
  phase: CampaignPhase;
  challenge: AnyChallenge;
  isBisCampaign: boolean;
  isEventChallenge: boolean;
}

type FrequencyHeaderImage = NonNullable<
  UnselectionDialog['frequencyHeaderImages']
>[keyof NonNullable<UnselectionDialog['frequencyHeaderImages']>];

interface ChallengeImage {
  path: string;
  type: 'logo' | 'banner' | '';
}

@Component({
  selector: 'b2c-unselect-challenge-modal',
  styleUrl: './unselect-challenge-modal.component.scss',
  templateUrl: './unselect-challenge-modal.component.html',
  imports: [SafePipe, ButtonComponent, TransformUrlToBgPipe],
})
export class UnselectChallengeModalComponent extends BaseComponent {
  public readonly OfferType: typeof OfferType = OfferType;

  public readonly config: UnselectionDialog =
    this.loyaltyChallengeConfig.components.modals.unselectChallenge;
  public readonly data: DialogData = inject<DialogConfig<DialogData>>(
    DIALOG_DATA
  ).data as DialogData;

  // The retailer config and the challenge are both fixed for the dialog's
  // lifetime, so everything derived from them resolves once here instead of on
  // every change-detection pass.
  public readonly frequencyHeaderImage = this.resolveFrequencyHeaderImage();
  public readonly challengeImage = this.resolveChallengeImage();
  public readonly titlePicture = this.frequencyHeaderImage
    ? undefined
    : this.config.title.picture;

  // `title.label` is empty only for the one retailer that shows no header, so
  // it doubles as "this retailer has a header". The coupling is deliberate:
  // keying this on the images instead would drop the banner for retailers that
  // currently render it empty.
  public readonly showHeader =
    !!this.config.title.label || !!this.frequencyHeaderImage;

  public readonly confirmButton = this.config.actionButtons.confirmButton;
  public readonly closeButton = this.config.actionButtons.closeButton;
  public readonly confirmButtonStyles = this.buttonStyles(this.confirmButton);
  public readonly closeButtonStyles: Dict = this.closeButton
    ? this.buttonStyles(this.closeButton)
    : {};

  public unselectionInProgress = false;

  private readonly dialogRef: DialogRef = inject(DialogRef);
  private readonly backendService: BackendService = inject(BackendService);

  /**
   * Unselect Challenge and close modal reference
   */
  confirmUnselection(): void {
    if (this.unselectionInProgress) {
      return;
    }
    this.unselectionInProgress = true;
    this.taggingService.emitTagForKey(EmitterKeys.CONFIRM_UNSELECT_CHALLENGE);
    this.backendService
      .unselectChallenge(
        this.data.challenge.campaignId,
        this.data.challenge.offerId
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const challenges = this.data.isEventChallenge
          ? this.dataService.currentEventData
          : this.phaseChallenges();

        challenges.set(
          challenges().map((challenge: AnyChallenge) =>
            challenge.offerId === this.data.challenge.offerId
              ? this.unselected(challenge)
              : challenge
          )
        );

        this.unselectionInProgress = false;
        this.dialogRef.close();
      });
  }

  /**
   * Close DialogRef Reference
   */
  closeDialog(): void {
    this.taggingService.emitTagForKey(EmitterKeys.CANCEL_UNSELECT_CHALLENGE);
    this.dialogRef.close();
  }

  /** The list the unselected challenge lives in, for this phase and campaign. */
  private phaseChallenges(): WritableSignal<AnyChallenge[]> {
    const isFuture = this.data.phase === CampaignPhase.FUTURE;

    if (this.data.isBisCampaign) {
      return isFuture
        ? this.dataService.futureBisChallenges
        : this.dataService.currentBisChallenges;
    }

    return isFuture
      ? this.dataService.futureChallenges
      : this.dataService.currentChallenges;
  }

  /**
   * Cloned rather than mutated: the challenge is a class instance, so the clone
   * keeps its methods while giving the list a new reference to diff on.
   */
  private unselected(challenge: AnyChallenge): AnyChallenge {
    const clone = Object.assign(
      Object.create(Object.getPrototypeOf(challenge)),
      challenge
    );
    clone.selectedDate = '';
    return clone;
  }

  /** Logo or banner of a step challenge; empty for every other type. */
  private resolveChallengeImage(): ChallengeImage {
    if (this.data.challenge.type !== OfferType.STEP) {
      return { path: '', type: '' };
    }

    const { productScope } = this.data.challenge;

    if (productScope.bannerPath) {
      return { path: productScope.bannerPath, type: 'banner' };
    }

    if (productScope.logoPath) {
      return { path: productScope.logoPath, type: 'logo' };
    }

    return { path: '', type: '' };
  }

  private resolveFrequencyHeaderImage(): FrequencyHeaderImage | undefined {
    if (this.data.challenge.type !== OfferType.FREQUENCY) {
      return undefined;
    }

    const images = this.config.frequencyHeaderImages;
    const headerImage =
      images?.[this.data.challenge.subType] ?? images?.default;

    return headerImage?.imageUrl ? headerImage : undefined;
  }

  private buttonStyles(button: DialogCloseButton): Dict {
    return {
      color: button.textColor,
      fontFamily: button.fontFamily,
      borderRadius: button.borderRadius,
      backgroundColor: button.backgroundColor,
      // A button with no border of its own takes the fill, so the edge matches.
      borderColor: button.borderColor || button.backgroundColor,
    };
  }
}
